/**
 * 知识抽查系统 - 知识看板（瀑布流布局）
 * 展示所有知识点，支持搜索和筛选，使用瀑布流布局
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Button, Tag, Input, Select, message, Empty, Spin, Row, Col } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, CalendarOutlined, SearchOutlined, BookOutlined, FilterOutlined } from '@ant-design/icons';
import knowledgeLotteryApi from '../services/knowledgeLotteryApi';
import './KnowledgeLotteryBoard.css';

const { Search } = Input;
const { Option } = Select;

const KnowledgeLotteryBoard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [knowledgeContents, setKnowledgeContents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filteredContents, setFilteredContents] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [columns, setColumns] = useState(3);
  const containerRef = useRef(null);
  const columnRefs = useRef([]);

  // 响应式列数
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width < 768) {
        setColumns(1);
      } else if (width < 1024) {
        setColumns(2);
      } else if (width < 1440) {
        setColumns(3);
      } else {
        setColumns(4);
      }
    };

    updateColumns();
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterContents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knowledgeContents, searchKeyword, selectedCategory]);

  // 瀑布流布局：将内容分配到列
  useEffect(() => {
    if (filteredContents.length === 0) return;

    // 初始化列高度数组
    const columnHeights = new Array(columns).fill(0);
    const columnContents = new Array(columns).fill(null).map(() => []);

    // 将内容分配到最短的列
    filteredContents.forEach((content) => {
      const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
      columnContents[shortestColumnIndex].push(content);
      // 估算高度（可以根据实际内容调整）
      const estimatedHeight = estimateCardHeight(content);
      columnHeights[shortestColumnIndex] += estimatedHeight;
    });

    // 更新列引用
    columnRefs.current = columnContents;
  }, [filteredContents, columns]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 获取所有知识点
      const contentsResponse = await knowledgeLotteryApi.knowledgeContentApi.getAllKnowledgeContents();
      console.log('知识点API响应:', contentsResponse);
      
      // 处理响应数据
      let contents = [];
      if (contentsResponse && contentsResponse.success) {
        contents = contentsResponse.data || [];
      } else if (Array.isArray(contentsResponse)) {
        // 兼容直接返回数组的情况
        contents = contentsResponse;
      } else if (contentsResponse && contentsResponse.data) {
        contents = Array.isArray(contentsResponse.data) ? contentsResponse.data : [];
      }

      setKnowledgeContents(contents);
      setFilteredContents(contents);

      // 获取所有分类
      try {
        const categoriesResponse = await knowledgeLotteryApi.categoryApi.getAllCategories();
        let categoriesData = [];
        if (categoriesResponse && categoriesResponse.success) {
          categoriesData = categoriesResponse.data || [];
        } else if (Array.isArray(categoriesResponse)) {
          categoriesData = categoriesResponse;
        } else if (categoriesResponse && categoriesResponse.data) {
          categoriesData = Array.isArray(categoriesResponse.data) ? categoriesResponse.data : [];
        }
        setCategories(categoriesData);
      } catch (catError) {
        console.error('获取分类失败:', catError);
        setCategories([]);
      }
    } catch (error) {
      console.error('获取数据失败:', error);
      message.error(error.response?.data?.message || error.message || '获取数据失败');
      setKnowledgeContents([]);
      setFilteredContents([]);
    } finally {
      setLoading(false);
    }
  };

  const filterContents = useCallback(() => {
    if (!knowledgeContents || knowledgeContents.length === 0) {
      setFilteredContents([]);
      return;
    }

    let filtered = [...knowledgeContents];

    // 按分类筛选
    if (selectedCategory) {
      filtered = filtered.filter(content => {
        const categoryId = typeof content.categoryId === 'object' 
          ? (content.categoryId?._id || content.categoryId?.id)
          : content.categoryId;
        return String(categoryId) === String(selectedCategory);
      });
    }

    // 按关键词搜索
    if (searchKeyword && searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase();
      filtered = filtered.filter(content => 
        (content.title && content.title.toLowerCase().includes(keyword)) ||
        (content.content && content.content.toLowerCase().includes(keyword))
      );
    }

    // 按学习日期倒序排序（优先使用关联会议的日期）
    filtered.sort((a, b) => {
      const dateA = a.associatedSession?.sessionDate 
        ? new Date(a.associatedSession.sessionDate)
        : (a.learnedDate ? new Date(a.learnedDate) : new Date(a.createdAt || 0));
      const dateB = b.associatedSession?.sessionDate 
        ? new Date(b.associatedSession.sessionDate)
        : (b.learnedDate ? new Date(b.learnedDate) : new Date(b.createdAt || 0));
      return dateB - dateA;
    });

    setFilteredContents(filtered);
  }, [knowledgeContents, searchKeyword, selectedCategory]);

  // 估算卡片高度（用于瀑布流布局）- 更精确的估算
  const estimateCardHeight = (content) => {
    const baseHeight = 180; // 基础高度（标题、标签、日期、按钮等）
    const titleLines = content.title ? Math.ceil(content.title.length / 20) : 1; // 每行约20字符
    const titleHeight = titleLines * 28; // 每行28px
    
    // 内容高度：根据内容长度估算，但设置最大限制
    const contentLength = content.content ? content.content.length : 0;
    const maxContentHeight = 300; // 最大内容高度
    const contentHeight = Math.min(
      Math.ceil(contentLength / 40) * 22, // 每行约40字符，每行22px
      maxContentHeight
    );
    
    return baseHeight + titleHeight + contentHeight;
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}年${month}月${day}日`;
  };

  // 判断知识点是否已学习（优先使用后端返回的isLearned字段，否则根据学习日期判断）
  const isLearned = (content) => {
    // 优先使用后端返回的isLearned字段（基于会议状态）
    if (content.isLearned !== undefined) {
      return content.isLearned;
    }
    // 如果没有isLearned字段，使用原有的逻辑（兼容旧数据）
    if (!content.learnedDate) return false;
    const learnedDate = new Date(content.learnedDate);
    const now = new Date();
    // 只比较日期部分，忽略时间
    learnedDate.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    return learnedDate < now;
  };

  const handleViewDetail = (contentId) => {
    navigate(`/knowledge-lottery/knowledge/${contentId}`);
  };

  const handleClearFilter = () => {
    setSearchKeyword('');
    setSelectedCategory(null);
  };

  return (
    <div className="knowledge-board-container">
      {/* Header */}
      <header className="knowledge-board-header">
        <div className="knowledge-board-header-content">
          <Button 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/knowledge-lottery')}
            className="back-button"
          >
            返回
          </Button>
          <h1 className="knowledge-board-title">知识看板</h1>
        </div>
      </header>

      <main className="knowledge-board-main">
        {/* 搜索和筛选 */}
        <Card className="knowledge-board-filter-card">
          <div className="knowledge-board-filters">
            <div className="filter-search">
              <Search
                placeholder="搜索知识点标题或内容..."
                allowClear
                enterButton={<SearchOutlined />}
                size="large"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onSearch={filterContents}
              />
            </div>
            <div className="filter-category">
              <Select
                placeholder="选择分类"
                allowClear
                size="large"
                style={{ width: '100%' }}
                value={selectedCategory}
                onChange={(value) => setSelectedCategory(value)}
                suffixIcon={<FilterOutlined />}
              >
                {categories.map(cat => (
                  <Option key={cat.id || cat._id} value={cat.id || cat._id}>
                    {cat.name}
                  </Option>
                ))}
              </Select>
            </div>
            {(searchKeyword || selectedCategory) && (
              <Button 
                onClick={handleClearFilter}
                size="large"
                type="default"
              >
                清除筛选
              </Button>
            )}
          </div>
        </Card>

        {/* 知识点瀑布流 */}
        {loading ? (
          <div className="knowledge-board-loading">
            <Spin size="large" />
            <div className="loading-text">加载中...</div>
          </div>
        ) : filteredContents.length === 0 ? (
          <Card className="knowledge-board-empty-card">
            <Empty 
              description={
                knowledgeContents.length === 0 
                  ? "暂无知识点" 
                  : searchKeyword || selectedCategory 
                    ? "没有找到匹配的知识点" 
                    : "暂无知识点"
              }
            />
          </Card>
        ) : (
          <>
            <div className="knowledge-board-masonry" ref={containerRef}>
              {Array.from({ length: columns }).map((_, columnIndex) => (
                <div key={columnIndex} className="masonry-column">
                  {columnRefs.current[columnIndex]?.map((content) => {
                    const categoryName = typeof content.categoryId === 'object' 
                      ? (content.categoryId?.name || content.categoryId?.categoryName)
                      : null;
                    
                    return (
                      <Card
                        key={content.id || content._id}
                        className="knowledge-card"
                        hoverable
                        onClick={() => handleViewDetail(content.id || content._id)}
                        bodyStyle={{ padding: '20px', height: '100%', display: 'flex', flexDirection: 'column' }}
                      >
                        <div className="knowledge-card-header">
                          <h3 className="knowledge-card-title">{content.title}</h3>
                        </div>
                        <div className="knowledge-card-tags">
                          {categoryName && (
                            <Tag color="blue" className="knowledge-tag">
                              {categoryName}
                            </Tag>
                          )}
                          {content.sessionYear && content.sessionNumber && (
                            <Tag color="red" className="knowledge-tag week-tag">
                              {content.sessionYear}年第{content.sessionNumber}期会议
                            </Tag>
                          )}
                        </div>
                        <div className="knowledge-card-date" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <CalendarOutlined /> {formatDate(
                            content.associatedSession?.sessionDate || 
                            content.learnedDate || 
                            content.createdAt
                          )}
                          {isLearned(content) && (
                            <Tag color="success" style={{ margin: 0 }}>已学习</Tag>
                          )}
                        </div>
                        <div className="knowledge-card-content">
                          <div className="knowledge-card-content-text">
                            {content.content}
                          </div>
                          {/* 当内容超过150字符时显示渐变遮罩 */}
                          {content.content && content.content.length > 150 && (
                            <div className="knowledge-card-content-fade" />
                          )}
                        </div>
                        <div className="knowledge-card-footer">
                          <Button 
                            type="link"
                            icon={<BookOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetail(content.id || content._id);
                            }}
                            className="view-detail-button"
                          >
                            查看详情
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              ))}
            </div>

            {/* 统计信息 */}
            <Card className="knowledge-board-stats-card">
              <div className="knowledge-board-stats">
                共找到 <strong className="stats-highlight">{filteredContents.length}</strong> 个知识点
                {knowledgeContents.length !== filteredContents.length && (
                  <span className="stats-total">（共 {knowledgeContents.length} 个）</span>
                )}
              </div>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default KnowledgeLotteryBoard;
